import React, { useMemo, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import apiUtils from "../utils/apiUtils";
import { getApiErrorMessage, isPolandCountry } from "../utils/taxOfficeUtils";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Required"),
  countryId: Yup.number().typeError("Required").required("Required"),
  taxOfficeNumber: Yup.string().when("countryId", {
    is: (countryId) => Number(countryId) === 17,
    then: Yup.string().required("Required").matches(/^\d{4}$/, "Must be 4 digits"),
    otherwise: Yup.string().notRequired(),
  }),
});

const buildInitialValues = (defaultCountryId) => ({
  name: "",
  street: "",
  buildingIdentifier: "",
  postalCode: "",
  city: "",
  countryId: defaultCountryId || "",
  taxOfficeNumber: "",
});

const CreateTaxOfficeDialog = ({ open, onClose, onCreated, defaultCountryId }) => {
  const countryData = apiUtils.useApiData("/api/v1/countries");
  const [submitError, setSubmitError] = useState("");
  const initialValues = useMemo(() => buildInitialValues(defaultCountryId), [defaultCountryId]);
  const countryLocked = Boolean(defaultCountryId);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitError("");

    try {
      const payload = {
        name: values.name,
        street: values.street,
        buildingIdentifier: values.buildingIdentifier,
        postalCode: values.postalCode,
        city: values.city,
        countryId: Number(values.countryId),
        taxOfficeNumber: values.taxOfficeNumber,
      };

      const response = await axios.post("/api/v1/taxOffices", payload);
      resetForm({ values: buildInitialValues(defaultCountryId) });
      onCreated(response.data);
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, "Failed to create tax office."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubmitError("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick") {
          return;
        }

        handleClose();
      }}
      fullWidth
      maxWidth="md"
      aria-labelledby="create-tax-office-dialog-title"
    >
      <DialogTitle id="create-tax-office-dialog-title">Add Tax Office</DialogTitle>
      <DialogContent dividers>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            errors,
            touched,
            values,
            isSubmitting,
            handleBlur,
            handleChange,
            handleSubmit: handleFormSubmit,
          }) => {
            const polandSelected = isPolandCountry(values.countryId, countryData);

            return (
              <Box component="form" onSubmit={handleFormSubmit} sx={{ pt: 1 }}>
                {submitError ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {submitError}
                  </Alert>
                ) : null}

                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Name"
                      value={values.name}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                      inputProps={{ "aria-label": "Tax office name" }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      name="countryId"
                      label="Country"
                      value={values.countryId}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      disabled={countryLocked}
                      error={Boolean(touched.countryId && errors.countryId)}
                      helperText={
                        (touched.countryId && errors.countryId) ||
                        (countryLocked ? "Country is locked to the selected customer." : "")
                      }
                      inputProps={{ "aria-label": "Country" }}
                    >
                      {countryData.map((country) => (
                        <MenuItem key={country.countryId} value={country.countryId}>
                          {country.countryCode} - {country.countryName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <TextField
                      fullWidth
                      name="street"
                      label="Street"
                      value={values.street}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.street && errors.street)}
                      helperText={touched.street && errors.street}
                      inputProps={{ "aria-label": "Street" }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      name="buildingIdentifier"
                      label="Building Number"
                      value={values.buildingIdentifier}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.buildingIdentifier && errors.buildingIdentifier)}
                      helperText={touched.buildingIdentifier && errors.buildingIdentifier}
                      inputProps={{ "aria-label": "Building number" }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      name="postalCode"
                      label="Postal Code"
                      value={values.postalCode}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.postalCode && errors.postalCode)}
                      helperText={touched.postalCode && errors.postalCode}
                      inputProps={{ "aria-label": "Postal code" }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="city"
                      label="City"
                      value={values.city}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.city && errors.city)}
                      helperText={touched.city && errors.city}
                      inputProps={{ "aria-label": "City" }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="taxOfficeNumber"
                      label={polandSelected ? "Tax Office Number" : "Tax Office Number (optional)"}
                      value={values.taxOfficeNumber}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.taxOfficeNumber && errors.taxOfficeNumber)}
                      helperText={touched.taxOfficeNumber && errors.taxOfficeNumber}
                      inputProps={{
                        "aria-label": "Tax office number",
                        maxLength: 20,
                      }}
                    />
                  </Grid>
                </Grid>

                <DialogActions sx={{ px: 0, pt: 3 }}>
                  <Button type="button" onClick={handleClose} disabled={isSubmitting}>
                    Close
                  </Button>
                  <Button type="submit" variant="contained" disabled={isSubmitting}>
                    {isSubmitting ? <CircularProgress size={18} /> : "Create Tax Office"}
                  </Button>
                </DialogActions>
              </Box>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTaxOfficeDialog;
